import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { FilesService } from '@appState/file-management/files/files.service';
import { getAllWithParams, StateDictionary } from '@appState/helpers/store-base';
import { downloadBlobUrlFileToBrowser } from '@highlander/common/helpers/file.helpers';
import {
  BcDevRequest,
  BcDevRequestFile,
  BcDevRequestUpdate,
  BcDevRequestUpdateStatusRequest,
} from '@models/products-and-orders/requests/bc-dev-request.model';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { RequestsStore } from '../requests.store';
import { BcDevRequestsService } from './bc-dev-requests.service';

type BcDevRequestsState = {
  files: BcDevRequestFile[];
  isLoading: boolean;
  request: BcDevRequest | null;
  isRequestLoading: boolean;
  updates: BcDevRequestUpdate[];
  isUpdatesLoading: boolean;
};

const initialState: BcDevRequestsState = {
  files: [],
  isLoading: false,
  request: null,
  isRequestLoading: false,
  updates: [],
  isUpdatesLoading: false,
};

const filesDictionary: StateDictionary<BcDevRequestFile> = {
  entityList: 'files',
  listLoading: 'isLoading',
  selectedEntity: '',
  selectedEntityLoading: '',
  createLoading: '',
  entityId: 'id',
  sortFn: (a, b) => a.name.localeCompare(b.name),
};

export const BcDevRequestsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('bc-dev-requests-store'),

  withMethods(
    (
      store,
      service = inject(BcDevRequestsService),
      filesService = inject(FilesService),
      router = inject(Router),
      requestsStore = inject(RequestsStore),
    ) => ({
      getFiles: getAllWithParams<BcDevRequestFile, string>({
        store,
        service: {
          getAll: (id: string) => service.getFiles(id),
        },
        dictionary: filesDictionary,
      }),

      deleteFile: rxMethod<{ id: string; fileId: string }>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true });
          }),
          switchMap(({ id, fileId }) =>
            service.deleteFile(id, fileId).pipe(
              tap(() => {
                const updatedFiles = store.files().filter(f => f.id !== fileId);
                patchState(store, {
                  files: updatedFiles,
                  isLoading: false,
                });
                if (updatedFiles.length === 0) {
                  router.navigate(['requests', requestsStore.selectedRequestId()]);
                }
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      downloadFile: rxMethod<{ fileId: string }>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true });
          }),
          switchMap(({ fileId }) =>
            filesService.getDownloadUrl(fileId).pipe(
              tap(url => {
                downloadBlobUrlFileToBrowser(url.URL);
                patchState(store, { isLoading: false });
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      downloadFileBulk: rxMethod<{ fileIds: string[] }>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true });
          }),
          switchMap(({ fileIds }) =>
            filesService.getFilesDownloadUrl(fileIds).pipe(
              tap(url => {
                downloadBlobUrlFileToBrowser(url.URL);
                patchState(store, { isLoading: false });
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      bulkDeleteFiles: rxMethod<{ id: string; fileIds: string[] }>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true });
          }),
          switchMap(({ id, fileIds }) =>
            service.bulkDeleteFiles(id, fileIds).pipe(
              tap(() => {
                const updatedFiles = store.files().filter(f => !fileIds.includes(f.id));
                patchState(store, {
                  files: updatedFiles,
                  isLoading: false,
                });
                if (updatedFiles.length === 0) {
                  router.navigate(['requests', requestsStore.selectedRequestId()]);
                }
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getOne: rxMethod<{ id: string }>(
        pipe(
          tap(() => {
            patchState(store, { isRequestLoading: true });
          }),
          switchMap(({ id }) =>
            service.getOne(id).pipe(
              tap(request => {
                patchState(store, { request, isRequestLoading: false });
              }),
              catchError(() => {
                patchState(store, { request: null, isRequestLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      updateStatus: rxMethod<{ id: string; data: BcDevRequestUpdateStatusRequest }>(
        pipe(
          tap(() => {
            patchState(store, { isRequestLoading: true });
          }),
          switchMap(({ id, data }) =>
            service.updateStatus(id, data).pipe(
              tap(request => {
                patchState(store, { request, isRequestLoading: false });
                router.navigate(['requests']);
                requestsStore.loadRequests();
              }),
              catchError(() => {
                patchState(store, { isRequestLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getUpdates: rxMethod<{ id: string }>(
        pipe(
          tap(() => {
            patchState(store, { isUpdatesLoading: true });
          }),
          switchMap(({ id }) =>
            service.getSubmittedUpdates(id).pipe(
              tap(updates => {
                const sortedUpdates = updates.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
                patchState(store, { updates: sortedUpdates, isUpdatesLoading: false });
              }),
              catchError(() => {
                patchState(store, { updates: [], isUpdatesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      resetState: () => {
        patchState(store, initialState);
      },
    }),
  ),
);

export interface BcDevRequestsStoreType {
  readonly files: Signal<BcDevRequestFile[]>;
  readonly isLoading: Signal<boolean>;
  readonly request: Signal<BcDevRequest | null>;
  readonly isRequestLoading: Signal<boolean>;
  readonly updates: Signal<BcDevRequestUpdate[]>;
  readonly isUpdatesLoading: Signal<boolean>;

  readonly getFiles: (id: string) => void;
  readonly deleteFile: (params: { id: string; fileId: string }) => void;
  readonly downloadFile: (params: { fileId: string }) => void;
  readonly downloadFileBulk: (params: { fileIds: string[] }) => void;
  readonly bulkDeleteFiles: (params: { id: string; fileIds: string[] }) => void;
  readonly getOne: (params: { id: string }) => void;
  readonly getUpdates: (params: { id: string }) => void;
  readonly updateStatus: (params: { id: string; data: BcDevRequestUpdateStatusRequest }) => void;
  readonly resetState: () => void;
}
